import { BackArrow, SaveIcon } from "allIcons";
import { useTags } from "api/siteSettings/Tags";
import { usePluginList } from "api/templateLibrary/pluginList";
import {
  useDeletePlugin,
  usePlugins,
  useUpdatePlugin,
} from "api/templateLibrary/plugins";
import {
  AddComponent,
  BodyContainer,
  DeleteButton,
  DropDownCard,
  HeaderContainer,
  IconWrapper,
  InputField,
  Loader,
  MainButton,
  PopOver,
  PreviewCard,
  PrimaryHeading,
  RightMainContainer,
  SecondaryHeading,
  SelectField,
  ToggleCard,
} from "commonUI";
import { ModulePrefixes } from "enums";
import { getUserSavePermission, isEqual, parse, useSearch, useUtilities } from "helpers";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const currencies = ["USD", "EUR", "GBP", "AUD", "CAD", "CHF", "SGD"];

export default function EditPlugin() {
  const { backUrl } = useSearch();
  const { notification, navigate } = useUtilities();
  const { data: plugins, isLoading } = usePlugins();
  const { mutate: updatePlugin, isLoading: updatingPlugin } = useUpdatePlugin();
  const { mutate: deletePlugin, isLoading: deletePluginLoading } =
    useDeletePlugin(backUrl ?? `/${ModulePrefixes.TEMPLATE_LIBRARY}/plugins`);
  const { data: pluginList } = usePluginList();

  const [openTags, setOpenTags] = useState(false);
  const [inputValues, setInputValues] = useState({
    plugin_title: "",
    license_key: "",
    enable_license_fee_for_invoicing: false,
    license_fee: "",
    license_billing_frequency: "Yearly",
    tags: [],
    plugin_type: 0,
    currency: "USD",
  });
  const { data: tags } = useTags();
  const { id: pluginId } = useParams();
  const { edit_templates, delete_templates } = useUserPermissions();

  useEffect(() => {
    if (plugins && Array.isArray(plugins)) {
      const plugin = plugins.find((ele) => isEqual(ele.id, pluginId));
      if (plugin) {
        setInputValues({
          ...plugin,
          tags: plugin.tags ? parse(plugin.tags) : [],
          currency: plugin.currency || "USD",
        });
      }
    }
  }, [plugins, pluginId]);

  const onInputChange = (key, value) => {
    setInputValues((pre) => ({ ...pre, [key]: value }));
  };

  const onSaveChanges = () => {
    if (!edit_templates) {
      notification({
        type: "error",
        message: t("libraryTemplate.plugins.error.notPermissionToEditThisPlugin"),
      });
      return;
    }
    updatePlugin(inputValues);
  };

  return (
    <>
      <RightMainContainer>
        <Loader show={updatingPlugin || deletePluginLoading} loader="block" />
        <HeaderContainer>
          <IconWrapper>
            <BackArrow
              onClick={() => navigate(backUrl ?? `/${ModulePrefixes.TEMPLATE_LIBRARY}/plugins`)}
            />
            <PrimaryHeading>{inputValues.plugin_title}</PrimaryHeading>
          </IconWrapper>
          <IconWrapper className="gap-14">
            <DeleteButton
              show={pluginId !== "add" && delete_templates}
              onDelete={() => {
                if (
                  pluginList.some((_) => {
                    return parse(_.plugin_list).some((__) =>
                      isEqual(__.id, pluginId)
                    );
                  })
                ) {
                  notification({
                    type: "error",
                    message: t("libraryTemplate.plugins.remove.failed.message"),
                  });
                  return;
                }
                deletePlugin(pluginId);
              }}
              deleteTitle={t("libraryTemplate.delete.title")}
              deleteButtonText={t("libraryTemplate.delete.button")}
              deleteMessage={t("libraryTemplate.delete.message")}
            />
            {getUserSavePermission({id: pluginId, editPermission: edit_templates}) ? (
              <MainButton disabled={isLoading} onClick={onSaveChanges}>
                <SaveIcon />
                {t("button.saveChanges")}
              </MainButton>
            ) : (
              ""
            )}
          </IconWrapper>
        </HeaderContainer>
        <BodyContainer>
          {isEqual(inputValues.plugin_type, 1) ? (
            <>
              <div className="flex flex-row gap-10 justify-between items-end mt-1 w-full">
                <InputField
                  label={t("libraryTemplate.plugins.input.licenseKey.label")}
                  description={t("libraryTemplate.plugins.input.licenseKey.description")}
                  value={inputValues.license_key}
                  className="flex-1"
                  setValue={(value) => onInputChange("license_key", value)}
                  placeholder={t("libraryTemplate.plugins.input.licenseKey.placeholder")}
                />
                <MainButton
                  disabled={isLoading || !inputValues.license_key}
                  className="mb-3-5"
                  onClick={() => {
                    navigator.clipboard.writeText(inputValues.license_key);
                    notification({
                      type: "SUCCESS",
                      message: t("common.copy.success.copiedOnClipboard"),
                    });
                  }}
                >
                  Copy
                </MainButton>
              </div>
              <ToggleCard
                label={t("libraryTemplate.plugins.input.enableLicenseFeeForInvoicing.label")}
                description={t("libraryTemplate.plugins.input.enableLicenseFeeForInvoicing.description")}
                value={inputValues.enable_license_fee_for_invoicing}
                setValue={(value) =>
                  onInputChange("enable_license_fee_for_invoicing", value)
                }
              />
              {inputValues.enable_license_fee_for_invoicing ? (
                <>
                  <div className="flex items-center gap-15 w-full">
                    <SelectField
                      label={t("libraryTemplate.plugins.input.currency.label")}
                      placeholder="USD"
                      value={inputValues.currency}
                      setValue={(value) => onInputChange("currency", value)}
                      style={{ minWidth: "100px" }}
                    >
                      {currencies.map((currency) => (
                        <option value={currency}>{currency}</option>
                      ))}
                    </SelectField>
                    <InputField
                      label={t("libraryTemplate.plugins.input.licenseFee.label")}
                      placeholder={t("libraryTemplate.plugins.input.licenseFee.placeholder")}
                      className="mt-2"
                      style={{ width: "calc(100% - 24px)" }}
                      value={inputValues.license_fee}
                      setValue={(value) => onInputChange("license_fee", value)}
                      type="number"
                    />
                  </div>
                  <SelectField
                    label={t("libraryTemplate.plugins.input.licenseBillingFrequency.label")}
                    placeholder={t("libraryTemplate.plugins.input.licenseBillingFrequency.options.yearly")}
                    value={inputValues.license_billing_frequency}
                    setValue={(value) =>
                      onInputChange("license_billing_frequency", value)
                    }
                  >
                    <option value="Yearly">{t("libraryTemplate.plugins.input.licenseBillingFrequency.options.yearly")}</option>
                    <option value="Monthly">{t("libraryTemplate.plugins.input.licenseBillingFrequency.options.monthly")}</option>
                  </SelectField>
                </>
              ) : (
                ""
              )}
            </>
          ) : (
            ""
          )}

          <div className="mt-2">
            <SecondaryHeading>{t("common.input.tags.label")}</SecondaryHeading>
            <div className="flex items-center gap-5 flex-wrap">
              {inputValues.tags.map((tag) => (
                <PreviewCard
                  color={tag.color}
                  title={tag.text}
                  icon={true}
                  className="m-0"
                  onCross={() => {
                    setInputValues((pre) => ({
                      ...pre,
                      tags: pre.tags.filter((ele) => ele.id !== tag.id),
                    }));
                  }}
                />
              ))}
            </div>
            <AddComponent
              title={t("common.input.tags.addTag")}
              value={openTags}
              onClick={() => setOpenTags(true)}
            >
              <PopOver
                open={openTags}
                onClose={() => setOpenTags(false)}
                toolTip={{
                  vertical: isEqual(inputValues.plugin_type, 1)
                    ? "bottom"
                    : "top",
                  horizontal: "center",
                }}
                style={{
                  bottom: isEqual(inputValues.plugin_type, 1)
                    ? "calc(100% + 15px)"
                    : "auto",
                  top: isEqual(inputValues.plugin_type, 1)
                    ? "auto"
                    : "calc(100% + 15px)",
                  right: "auto",
                  left: "auto",
                }}
              >
                <DropDownCard
                  title={t("common.input.tags.label")}
                  listStyle
                  filterSelectedItems
                  className="relative-important"
                  selectedValues={inputValues.tags.map((item) => item.id)}
                  list={tags?.map((ele) => ({
                    ...ele,
                    index: ele.id,
                    text: ele.tag_title,
                  }))}
                  onSelect={(item) => {
                    if (
                      inputValues.tags.find(
                        (i) => Number(i.id) === Number(item.id)
                      )
                    ) {
                      return;
                    }
                    setInputValues((pre) => ({
                      ...pre,
                      tags: [
                        ...pre.tags,
                        {
                          id: item.id,
                          text: item.tag_title,
                          color: item.color,
                        },
                      ],
                    }));
                  }}
                />
              </PopOver>
            </AddComponent>
          </div>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
